import { useMemo, useState } from 'react';

const FALLBACK_ERROR_MESSAGE =
  'An error occurred while generating the description. Please try contacting support if the issue persists.';

type GenerateDescriptionState = {
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  reasonForNotBeingAbleToGenerateDescription?: string;
};

const setIsLoading = (
  isLoading: boolean,
  state: GenerateDescriptionState
): GenerateDescriptionState => ({
  ...state,
  isLoading,
});

const setError = (
  state: GenerateDescriptionState,
  errorMessage: string | undefined,
  defaultErrorMessage: string
): GenerateDescriptionState => ({
  ...state,
  hasError: true,
  errorMessage: errorMessage ?? defaultErrorMessage,
});

const setReasonForNotBeingAbleToGenerateDescription = (
  state: GenerateDescriptionState,
  reasonForNotBeingAbleToGenerateDescription?: string
) => ({
  ...state,
  reasonForNotBeingAbleToGenerateDescription,
});

const defaultUseGenerateDescriptionState: GenerateDescriptionState = {
  isLoading: false,
  hasError: false,
};

const resetState = (): GenerateDescriptionState => ({
  ...defaultUseGenerateDescriptionState,
});

export const useGenerateDescriptionState = (
  defaultErrorMessage: string = FALLBACK_ERROR_MESSAGE
) => {
  const [state, setState] = useState<GenerateDescriptionState>(
    defaultUseGenerateDescriptionState
  );
  // Stable identity, so callers can memoise on it. That means every command
  // has to reach for the current state through the updater form rather than
  // closing over the render's `state`.
  const commands = useMemo(
    () => ({
      onLoading: () => setState(state => setIsLoading(true, state)),
      onError: (errorMessage: string | undefined) =>
        setState(state => setError(state, errorMessage, defaultErrorMessage)),
      onDescriptionReceived: () => setState(resetState()),
      onLLMCouldNotGenerateDescription: (
        reasonForNotBeingAbleToGenerateDescription: string
      ) =>
        setState(state =>
          setReasonForNotBeingAbleToGenerateDescription(
            state,
            reasonForNotBeingAbleToGenerateDescription
          )
        ),
    }),
    [defaultErrorMessage]
  );

  return {
    state,
    commands,
  };
};
